import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useAuth } from "../../../providers/authprovider";

const COLUMN_NAME = "protocol_arrival_signature";

function parseColumnValue(rawValue) {
  if (rawValue === null || rawValue === undefined) return [];
  if (Array.isArray(rawValue)) {
    return rawValue.filter((v) => typeof v === "string");
  }

  if (typeof rawValue === "string") {
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        return parsed.filter((v) => typeof v === "string");
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  return [];
}

export default function ProtocolArrivalSignature({ settingsData, onSettingsSaved }) {
  const { settings } = useAuth();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusText, setStatusText] = useState("");
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const saveTimerRef = useRef(null);
  const dragStartItemsRef = useRef([]);
  const droppedRef = useRef(false);

  const sourceItems = useMemo(
    () => parseColumnValue(settingsData?.[COLUMN_NAME]),
    [settingsData],
  );

  useEffect(() => {
    setItems(sourceItems);
  }, [sourceItems]);

  const saveToApi = async (nextItems) => {
    setSaving(true);
    setError("");
    setStatusText("");

    const result = await settings.saveSetting({
      column: COLUMN_NAME,
      json: nextItems,
    });

    setSaving(false);

    if (!result?.success) {
      setError(result?.error || "Nie udało się zapisać ustawienia.");
      return;
    }

    setStatusText("Zapisano.");
    onSettingsSaved?.(result.content || {});
  };

  const scheduleSave = (nextItems) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      saveToApi(nextItems);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleAdd = () => {
    const next = [...items, ""];
    setItems(next);
    scheduleSave(next);
  };

  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    scheduleSave(next);
  };

  const handleEdit = (index, value) => {
    const next = [...items];
    next[index] = value;
    setItems(next);
    scheduleSave(next);
  };

  const handleDragStart = (index, event) => {
    dragStartItemsRef.current = [...items];
    droppedRef.current = false;
    setDragFromIndex(index);
    setIsDragging(true);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (index, event) => {
    event.preventDefault();

    if (!isDragging || dragFromIndex === null || dragFromIndex === index) return;

    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragFromIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragFromIndex(index);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    droppedRef.current = true;
    setDragFromIndex(null);
    setIsDragging(false);
    scheduleSave(items);
  };

  const handleDragEnd = () => {
    if (!droppedRef.current) {
      // Drag cancelled outside drop zone -> restore original order
      setItems(dragStartItemsRef.current);
    }
    setDragFromIndex(null);
    setIsDragging(false);
  };

  return (
    <div className="p-3 border rounded bg-light mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Cel przyjazdu (Protokoły)</h5>
        <Button size="sm" onClick={handleAdd}>
          Dodaj pozycję
        </Button>
      </div>

      {items.length === 0 && (
        <div className="text-muted mb-2">Brak pozycji. Dodaj pierwszą wartość.</div>
      )}

      {items.map((item, index) => (
        <div
          key={`arrival-purpose-${index}`}
          className={`mb-2 rounded ${isDragging && dragFromIndex === index ? "border border-primary" : ""}`}
          onDragOver={(e) => handleDragOver(index, e)}
          onDrop={handleDrop}
        >
          <InputGroup className="mb-0">
            <InputGroup.Text style={{ minWidth: 40 }}>{index + 1}</InputGroup.Text>
            <Button
              variant="outline-secondary"
              draggable
              onDragStart={(e) => handleDragStart(index, e)}
              onDragEnd={handleDragEnd}
              title="Przeciągnij, aby zmienić kolejność"
              style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
              ::
            </Button>
            <Form.Control
              value={item}
              placeholder="Wpisz cel przyjazdu"
              onChange={(e) => handleEdit(index, e.target.value)}
            />
            <Button variant="outline-danger" onClick={() => handleRemove(index)}>
              Usuń
            </Button>
          </InputGroup>
        </div>
      ))}

      <div className="mt-2 d-flex align-items-center gap-2">
        {saving && (
          <span className="text-muted">
            <Spinner size="sm" className="me-2" />
            Zapisywanie...
          </span>
        )}
        {!saving && statusText && <span className="text-success">{statusText}</span>}
      </div>

      {error && (
        <Alert variant="danger" className="mt-2 mb-0">
          {error}
        </Alert>
      )}
    </div>
  );
}
